import { useEffect, useReducer, useRef } from "react";
import type { FullRecord, RecordInput } from "@shared/types";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";
import { fmtS, formatHistoryDate } from "../util";
import { RAMOS, MOTIVOS_PERDA } from "../constants";
import { reducer, initialState } from "./EditModalReducer";

interface Props {
  recordId: number | null;
  records: FullRecord[];
  onUpsert: (r: FullRecord) => void;
  onRemove: (id: number) => void;
  onClose: () => void;
}

export function EditModal({ recordId, records, onUpsert, onRemove, onClose }: Props) {
  const auth = useAuth();
  const existing = recordId != null ? records.find(r => r.id === recordId) || null : null;
  const [state, dispatch] = useReducer(reducer, initialState(existing));
  const histInputRef = useRef<HTMLTextAreaElement>(null);
  const dNomeRef = useRef<HTMLInputElement>(null);
  const dUrlRef = useRef<HTMLInputElement>(null);
  const knownVersionRef = useRef<number>(existing?.version ?? -1);

  useEffect(() => {
    if (recordId == null) return;
    const fresh = records.find(r => r.id === recordId);
    if (!fresh) return;
    if (fresh.version !== knownVersionRef.current) {
      knownVersionRef.current = fresh.version;
      dispatch({ type: "replaceFromServer", record: fresh });
    }
  }, [records, recordId]);

  const isPerdido = state.form.status === "perdido";

  function setField(field: keyof typeof state.form, value: string) {
    dispatch({ type: "setField", field, value });
  }

  function sugerirData() {
    const n = new Date();
    setField("reativ_data", `${n.getFullYear() + 1}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`);
  }

  async function save() {
    if (!state.form.cliente.trim()) { alert("Informe o nome do cliente."); return; }
    dispatch({ type: "setSaving", value: true });
    try {
      const payload: RecordInput = {
        data: state.form.data ? fmtS(state.form.data) : "",
        resp: state.form.resp.trim(),
        cliente: state.form.cliente.trim(),
        ramo: state.form.ramo,
        status: state.form.status,
        corretor: state.form.corretor.trim(),
        prox_data: state.form.prox_data,
        prox_mot: state.form.prox_mot.trim(),
        obs: state.form.obs.trim(),
        motivo_perda: state.form.motivo_perda,
        motivo_det: state.form.motivo_det.trim(),
        reativ_data: state.form.reativ_data,
      };
      let result;
      if (recordId != null) {
        const cur = records.find(r => r.id === recordId);
        result = await api.updateRecord(recordId, payload, cur?.version ?? 0);
      } else {
        result = await api.createRecord(payload);
        if (result) {
          let v = result.record.version;
          const failures: string[] = [];
          for (const h of state.tempHist) {
            try {
              const r = await api.addHistory(result.record.id, h.txt, v);
              if (r) { result.record = r.record; v = r.record.version; }
            } catch (e: any) {
              failures.push(`Histórico "${h.txt.substring(0, 30)}…": ${e.message || e}`);
            }
          }
          for (const d of state.tempDocs) {
            try {
              const r = await api.addDoc(result.record.id, d.nome, d.url, v);
              if (r) { result.record = r.record; v = r.record.version; }
            } catch (e: any) {
              failures.push(`Documento "${d.nome}": ${e.message || e}`);
            }
          }
          if (failures.length) {
            alert("Registro criado, mas estas entradas falharam:\n\n" + failures.join("\n"));
          }
        }
      }
      if (result) {
        knownVersionRef.current = result.record.version;
        onUpsert(result.record);
      }
      onClose();
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 409 && e.body?.currentRecord) {
        knownVersionRef.current = e.body.currentRecord.version;
        onUpsert(e.body.currentRecord);
        dispatch({ type: "replaceFromServer", record: e.body.currentRecord });
        dispatch({ type: "setConflict", value: true });
      } else {
        alert("Erro ao salvar: " + (e.message || e));
      }
    } finally {
      dispatch({ type: "setSaving", value: false });
    }
  }

  async function deleteItem() {
    if (recordId == null) return;
    if (!confirm("Excluir esta prospecção?")) return;
    const cur = records.find(r => r.id === recordId);
    try {
      await api.deleteRecord(recordId, cur?.version ?? 0);
      onRemove(recordId);
      onClose();
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 409 && e.body?.currentRecord) {
        knownVersionRef.current = e.body.currentRecord.version;
        onUpsert(e.body.currentRecord);
        dispatch({ type: "replaceFromServer", record: e.body.currentRecord });
        dispatch({ type: "setConflict", value: true });
      } else {
        alert("Erro ao excluir: " + (e.message || e));
      }
    }
  }

  async function addHist() {
    const txt = (histInputRef.current?.value || "").trim();
    if (!txt) return;
    if (recordId == null) {
      dispatch({ type: "addTempHist", entry: { dt: formatHistoryDate(), txt, author_name: auth.user.display_name } });
      if (histInputRef.current) histInputRef.current.value = "";
      return;
    }
    const cur = records.find(r => r.id === recordId);
    try {
      const r = await api.addHistory(recordId, txt, cur?.version ?? 0);
      if (r) {
        knownVersionRef.current = r.record.version;
        onUpsert(r.record);
        dispatch({ type: "replaceFromServer", record: r.record });
      }
      if (histInputRef.current) histInputRef.current.value = "";
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 409 && e.body?.currentRecord) {
        knownVersionRef.current = e.body.currentRecord.version;
        onUpsert(e.body.currentRecord);
        dispatch({ type: "replaceFromServer", record: e.body.currentRecord });
        dispatch({ type: "setConflict", value: true });
      } else alert("Erro: " + e.message);
    }
  }

  function delHist(i: number) {
    if (recordId != null) { alert("Histórico salvo não pode ser apagado (auditoria)."); return; }
    dispatch({ type: "delTempHist", index: i });
  }

  async function addDoc() {
    const nome = (dNomeRef.current?.value || "").trim();
    const url = (dUrlRef.current?.value || "").trim();
    if (!nome) { alert("Informe o nome do documento."); return; }
    if (!url || !/^https?:\/\//i.test(url)) { alert("Cole um link válido começando com http:// ou https://"); return; }
    if (recordId == null) {
      dispatch({ type: "addTempDoc", doc: { nome, url } });
      if (dNomeRef.current) dNomeRef.current.value = "";
      if (dUrlRef.current) dUrlRef.current.value = "";
      return;
    }
    const cur = records.find(r => r.id === recordId);
    try {
      const r = await api.addDoc(recordId, nome, url, cur?.version ?? 0);
      if (r) {
        knownVersionRef.current = r.record.version;
        onUpsert(r.record);
        dispatch({ type: "replaceFromServer", record: r.record });
      }
      if (dNomeRef.current) dNomeRef.current.value = "";
      if (dUrlRef.current) dUrlRef.current.value = "";
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 409 && e.body?.currentRecord) {
        knownVersionRef.current = e.body.currentRecord.version;
        onUpsert(e.body.currentRecord);
        dispatch({ type: "replaceFromServer", record: e.body.currentRecord });
        dispatch({ type: "setConflict", value: true });
      } else alert("Erro: " + e.message);
    }
  }

  async function delDoc(i: number) {
    const doc = state.tempDocs[i];
    if (!doc) return;
    if (recordId == null || doc.id == null) {
      dispatch({ type: "delTempDoc", index: i });
      return;
    }
    const cur = records.find(r => r.id === recordId);
    try {
      const r = await api.deleteDoc(recordId, doc.id, cur?.version ?? 0);
      if (r) {
        knownVersionRef.current = r.record.version;
        onUpsert(r.record);
        dispatch({ type: "replaceFromServer", record: r.record });
      }
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 409 && e.body?.currentRecord) {
        knownVersionRef.current = e.body.currentRecord.version;
        onUpsert(e.body.currentRecord);
        dispatch({ type: "replaceFromServer", record: e.body.currentRecord });
        dispatch({ type: "setConflict", value: true });
      } else alert("Erro: " + e.message);
    }
  }

  return (
    <div className="overlay open" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <h2>{recordId != null ? "Editar prospecção" : "Nova prospecção"}</h2>

        {state.conflict && (
          <div style={{ background: "#FAEEDA", border: ".5px solid #EF9F27", color: "#854F0B", padding: "10px 12px", borderRadius: 10, fontSize: 12, marginBottom: 14 }}>
            Este registro foi alterado por outro usuário. Confira os campos abaixo (com os dados atualizados) e salve novamente se ainda quiser sobrescrever.
          </div>
        )}

        <div className="tabs">
          <button className={"tab" + (state.tab === "dados" ? " active" : "")} onClick={() => dispatch({ type: "setTab", tab: "dados" })}>Dados gerais</button>
          <button className={"tab" + (state.tab === "historico" ? " active" : "")} onClick={() => dispatch({ type: "setTab", tab: "historico" })}>Histórico</button>
          <button className={"tab" + (state.tab === "docs" ? " active" : "")} onClick={() => dispatch({ type: "setTab", tab: "docs" })}>Documentos</button>
        </div>

        {state.tab === "dados" && (
          <div className="tp active">
            <div className="f2">
              <div className="fr"><label>Data de entrada</label><input type="date" value={state.form.data} onChange={e => setField("data", e.target.value)} /></div>
              <div className="fr"><label>Responsável</label><input type="text" placeholder="Ex: sintia, DAI" value={state.form.resp} onChange={e => setField("resp", e.target.value)} /></div>
            </div>
            <div className="fr"><label>Cliente *</label><input type="text" placeholder="Nome do cliente ou empresa" value={state.form.cliente} onChange={e => setField("cliente", e.target.value)} /></div>
            <div className="f2">
              <div className="fr"><label>Ramo</label>
                <select value={state.form.ramo} onChange={e => setField("ramo", e.target.value)}>
                  {RAMOS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="fr"><label>Status</label>
                <select value={state.form.status} onChange={e => setField("status", e.target.value)}>
                  <option value="novo">Novo</option>
                  <option value="cotacao">Cotação enviada</option>
                  <option value="analise">Em análise</option>
                  <option value="efetivado">Efetivado</option>
                  <option value="perdido">Não efetivado</option>
                </select>
              </div>
            </div>
            <div className="fr"><label>Corretor / Indicação</label><input type="text" placeholder="Nome do corretor ou origem" value={state.form.corretor} onChange={e => setField("corretor", e.target.value)} /></div>
            <div className="f2">
              <div className="fr"><label>Próximo contato</label><input type="date" value={state.form.prox_data} onChange={e => setField("prox_data", e.target.value)} /></div>
              <div className="fr"><label>Motivo do contato</label><input type="text" placeholder="Ex: ligar para fechar proposta" value={state.form.prox_mot} onChange={e => setField("prox_mot", e.target.value)} /></div>
            </div>
            <div className="fr"><label>Observação atual</label><textarea placeholder="Detalhes, pendências, próximos passos..." value={state.form.obs} onChange={e => setField("obs", e.target.value)} /></div>

            {isPerdido && (
              <div className="pbox">
                <legend>Não efetivado — detalhes</legend>
                <div className="fr" style={{ marginBottom: 10 }}>
                  <label>Motivo de não fechamento</label>
                  <select value={state.form.motivo_perda} onChange={e => setField("motivo_perda", e.target.value)}>
                    <option value="">Selecione o motivo...</option>
                    {MOTIVOS_PERDA.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="fr" style={{ marginBottom: 12 }}>
                  <label>Detalhe adicional (opcional)</label>
                  <input type="text" placeholder="Ex: disse que vai revisar no próximo ano" value={state.form.motivo_det} onChange={e => setField("motivo_det", e.target.value)} />
                </div>
                <div className="fr" style={{ marginBottom: 0 }}>
                  <label>Agendar reativação para o próximo ano</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                    <input type="date" value={state.form.reativ_data} onChange={e => setField("reativ_data", e.target.value)} style={{ flex: 1 }} />
                    <button type="button" className="btn-sm" onClick={sugerirData}>
                      <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                      Sugerir data
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--tx3)", marginTop: 5 }}>Quando a data chegar, o cliente aparecerá no painel de reativação no topo da tela.</div>
                </div>
              </div>
            )}
          </div>
        )}

        {state.tab === "historico" && (
          <div className="tp active">
            <p style={{ fontSize: 12, color: "var(--tx3)", marginBottom: 12 }}>Registro cronológico de todas as tratativas com este cliente.</p>
            <div className="hlist">
              {state.tempHist.length === 0 ? (
                <div style={{ fontSize: 13, color: "var(--tx3)", textAlign: "center", padding: "1rem" }}>Nenhuma tratativa registrada ainda.</div>
              ) : (
                [...state.tempHist].reverse().map((h, ri) => {
                  const idx = state.tempHist.length - 1 - ri;
                  return (
                    <div key={idx} className="hitem">
                      <div className="hdt">{h.dt}{h.author_name ? <> · <span style={{ color: "var(--tx2)" }}>{h.author_name}</span></> : null}</div>
                      <div className="htx">{h.txt}</div>
                      {recordId == null && (
                        <button className="hdel" onClick={() => delHist(idx)}>
                          <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div className="hadd">
              <textarea ref={histInputRef} placeholder="Descreva a tratativa: ligação, reunião, e-mail enviado, resposta recebida..." />
              <button className="btn" style={{ alignSelf: "flex-end", padding: "9px 12px" }} onClick={addHist}>
                <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              </button>
            </div>
          </div>
        )}

        {state.tab === "docs" && (
          <div className="tp active">
            <p style={{ fontSize: 12, color: "var(--tx3)", marginBottom: 12 }}>Cole links de documentos do Google Drive, e-mail, WhatsApp ou qualquer plataforma.</p>
            <div className="dlist">
              {state.tempDocs.length === 0 ? (
                <div style={{ fontSize: 13, color: "var(--tx3)", textAlign: "center", padding: "1rem 0 0.5rem" }}>Nenhum documento vinculado ainda.</div>
              ) : (
                state.tempDocs.map((d, i) => (
                  <div key={i} className="ditem">
                    <div className="d-ico">
                      <svg viewBox="0 0 24 24"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                    </div>
                    <div className="d-info">
                      <div className="d-nome">{d.nome}</div>
                      <div className="d-url">{d.url}</div>
                    </div>
                    <div className="d-acts">
                      <button className="btn-ico" title="Abrir link" onClick={() => { if (/^https?:\/\//i.test(d.url)) window.open(d.url, "_blank", "noopener,noreferrer"); }}>
                        <svg viewBox="0 0 24 24"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                      </button>
                      <button className="btn-ico" title="Remover" onClick={() => delDoc(i)}>
                        <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="dadd-box">
              <div className="dadd-lbl">Adicionar documento</div>
              <input type="text" placeholder="Nome do documento (ex: Proposta Tokio 03/25, Apólice anterior)" ref={dNomeRef} />
              <div className="dadd-row">
                <input type="text" placeholder="Cole o link aqui (Google Drive, WhatsApp Web, e-mail...)" ref={dUrlRef} style={{ flex: 1, marginBottom: 0 }} />
                <button className="btn-sm btn-p" style={{ padding: "9px 14px" }} onClick={addDoc}>
                  <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  Adicionar
                </button>
              </div>
              <div className="dhint">Dica: no Google Drive, clique com o botão direito no arquivo → "Copiar link" e cole acima. Funciona também com links de e-mail, WhatsApp Web, Dropbox e outros.</div>
            </div>
          </div>
        )}

        <div className="mfoot">
          {recordId != null && (
            <button className="btn-del" style={{ display: "inline-flex" }} onClick={deleteItem}>
              <svg viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" /></svg>
              Excluir
            </button>
          )}
          <button className="btn-cancel" onClick={onClose}>Cancelar</button>
          <button className="btn-save" onClick={save} disabled={state.saving}>Salvar</button>
        </div>
      </div>
    </div>
  );
}
